import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, ButtonGroup, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { FaLayerGroup, FaPlus, FaSave, FaSitemap, FaTrash, FaShieldAlt, FaLock } from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import { getFirestore, doc, getDoc, collection, getDocs, query, orderBy } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

const db = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, "us-central1");

const GRADIENT = "linear-gradient(135deg, #4c1d95 0%, #7c3aed 60%, #a78bfa 100%)";
const PURPLE700 = "#6d28d9";
const PURPLE100 = "#ede9fe";
const ACCENT = "#f59e0b";

const PROCESS_OPTIONS = [
  { value: "blacklist_removal", label: "Salida de Lista Negra" },
  { value: "attendance_approval", label: "Aprobaciones de Asistencia" },
  { value: "control_papeletas", label: "Control de Papeletas" },
  { value: "user_create", label: "Alta de Usuario" },
  { value: "user_reactivation", label: "Reactivación de Usuario" },
  { value: "purchase_order_approval", label: "Aprobación Orden de Compra" },
  { value: "payment_approval", label: "Aprobación de Pagos" },
];

const DEFAULT_ROLE_OPTIONS = [
  { code: "admin", label: "admin", order: 1 },
  { code: "rh", label: "RH", order: 2 },
  { code: "supervisor", label: "supervisor", order: 3 },
  { code: "analista", label: "analista", order: 4 },
  { code: "asistente", label: "asistente", order: 5 },
  { code: "seguridad", label: "seguridad", order: 6 },
  { code: "medico", label: "medico", order: 7 },
  { code: "almacenero", label: "almacenero", order: 8 },
  { code: "capacitador", label: "capacitador", order: 9 },
  { code: "ti", label: "TI", order: 10 },
  { code: "planner", label: "planner", order: 11 },
  { code: "admin_nomina", label: "nomina", order: 12 },
  { code: "chofer", label: "chofer", order: 13 },
];
const NON_ASSIGNABLE_ROLES = new Set(["superAdmin"]);
const ensurePayrollRoleOption = (options = []) => {
  const hasPayroll = options.some((r) => String(r.code || "").trim().toLowerCase() === "admin_nomina");
  if (hasPayroll) return options;
  return [...options, { code: "admin_nomina", label: "nomina", order: 998 }];
};
const ensurePlannerRoleOption = (options = []) => {
  const hasPlanner = options.some((r) => String(r.code || "").trim().toLowerCase() === "planner");
  if (hasPlanner) return options;
  return [...options, { code: "planner", label: "planner", order: 997 }];
};
const ensureChoferRoleOption = (options = []) => {
  const hasChofer = options.some((r) => String(r.code || "").trim().toLowerCase() === "chofer");
  if (hasChofer) return options;
  return [...options, { code: "chofer", label: "chofer", order: 996 }];
};
const sortRoleOptionsAlpha = (options = []) =>
  [...options].sort((a, b) => String(a.label || a.code || "").localeCompare(String(b.label || b.code || ""), "es", { sensitivity: "base" }));

function AprobacionesNiveles({ usuario }) {
  const [processType, setProcessType] = useState("blacklist_removal");
  const [levels, setLevels] = useState([
    { level: 1, roles: ["rh"], minApprovers: 1 },
    { level: 2, roles: ["admin"], minApprovers: 1 },
  ]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adminBypassEnabled, setAdminBypassEnabled] = useState(false);
  const [roleOptions, setRoleOptions] = useState(DEFAULT_ROLE_OPTIONS);

  const tenantId = String(usuario?.ruc || "").trim();
  const canManage = useMemo(() => ["admin", "superAdmin"].includes(String(usuario?.rol || "")), [usuario]);

  const loadRoleOptions = async () => {
    try {
      let snap = await getDocs(query(collection(db, `governance/${tenantId}/rolesCatalog`), orderBy("order", "asc")));
      if (snap.empty) {
        snap = await getDocs(query(collection(db, "rolesCatalog"), orderBy("order", "asc")));
      }
      if (snap.empty) { setRoleOptions(sortRoleOptionsAlpha(ensureChoferRoleOption(ensurePlannerRoleOption(ensurePayrollRoleOption(DEFAULT_ROLE_OPTIONS))))); return; }
      const list = snap.docs
        .map((d) => { const data = d.data() || {}; return { code: String(data.code || d.id || "").trim(), label: String(data.label || data.code || d.id || "").trim(), order: Number(data.order ?? 999) }; })
        .filter((r) => r.code)
        .filter((r) => !NON_ASSIGNABLE_ROLES.has(String(r.code || "").trim()));
      setRoleOptions(sortRoleOptionsAlpha(ensureChoferRoleOption(ensurePlannerRoleOption(ensurePayrollRoleOption(list.length ? list : DEFAULT_ROLE_OPTIONS)))));
    } catch (e) { setRoleOptions(sortRoleOptionsAlpha(ensureChoferRoleOption(ensurePlannerRoleOption(ensurePayrollRoleOption(DEFAULT_ROLE_OPTIONS))))); }
  };

  const loadPolicy = async (selectedProcess) => {
    if (!tenantId || !selectedProcess) return;
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, `governance/${tenantId}/approvalPolicies/${selectedProcess}`));
      if (!snap.exists()) { setAdminBypassEnabled(false); setLevels([{ level: 1, roles: ["rh"], minApprovers: 1 }, { level: 2, roles: ["admin"], minApprovers: 1 }]); return; }
      const data = snap.data() || {};
      const mapped = (data.levels || []).map((lvl) => ({ level: Number(lvl.level || 1), roles: Array.isArray(lvl.roles) ? lvl.roles.map((r) => String(r).trim()).filter(Boolean) : [], minApprovers: Number(lvl.minApprovers || 1) })).sort((a, b) => a.level - b.level);
      const rolesFromPolicy = mapped.flatMap((lvl) => lvl.roles || []);
      setRoleOptions((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const prevCodes = new Set(prevArr.map((r) => String(r.code)));
        const extras = rolesFromPolicy
          .filter((code) => !prevCodes.has(String(code)))
          .filter((code) => !NON_ASSIGNABLE_ROLES.has(String(code || "").trim()))
          .map((code) => ({ code: String(code), label: String(code), order: 999 }));
        return sortRoleOptionsAlpha([...prevArr, ...extras]);
      });
      const constraints = data.constraints || {};
      setAdminBypassEnabled((constraints.bypassRoles || []).includes("admin"));
      setLevels(mapped.length ? mapped : [{ level: 1, roles: ["rh"], minApprovers: 1 }]);
    } catch (e) { Swal.fire("Error", "No se pudo cargar la política.", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRoleOptions(); }, [tenantId]); // eslint-disable-line
  useEffect(() => { loadPolicy(processType); }, [processType, tenantId]); // eslint-disable-line

  const updateLevel = (idx, key, value) => setLevels((prev) => prev.map((row, i) => i === idx ? { ...row, [key]: value } : row));
  const toggleLevelRole = (idx, role, checked) => setLevels((prev) => prev.map((row, i) => { if (i !== idx) return row; const roles = Array.isArray(row.roles) ? row.roles : []; return { ...row, roles: checked ? Array.from(new Set([...roles, role])) : roles.filter((r) => r !== role) }; }));
  const addLevel = () => setLevels((prev) => [...prev, { level: prev.length + 1, roles: ["admin"], minApprovers: 1 }]);
  const removeLevel = (idx) => setLevels((prev) => prev.filter((_, i) => i !== idx).map((row, i) => ({ ...row, level: i + 1 })));
  const applySingleLevelAdminPreset = () => setLevels([{ level: 1, roles: ["admin"], minApprovers: 1 }]);
  const applyTwoLevelRhAdminPreset = () => setLevels([{ level: 1, roles: ["rh"], minApprovers: 1 }, { level: 2, roles: ["admin"], minApprovers: 1 }]);

  const savePolicy = async () => {
    if (!canManage) return;
    const normalized = levels.map((lvl, i) => ({ level: i + 1, roles: Array.isArray(lvl.roles) ? lvl.roles.map((r) => String(r).trim()).filter(Boolean) : [], minApprovers: Number(lvl.minApprovers || 1) }));
    if (normalized.some((l) => l.roles.length === 0 || l.minApprovers <= 0)) { Swal.fire("Validación", "Cada nivel requiere roles y mínimo de aprobadores mayor a 0.", "warning"); return; }
    setSaving(true);
    try {
      const callable = httpsCallable(functions, "upsertTenantApprovalPolicy");
      await callable({ tenantId, processType, policy: { levels: normalized, constraints: { separationOfDuties: true, sameApproverAcrossLevels: false, bypassRoles: adminBypassEnabled ? ["admin"] : [], requireBypassReason: true, allowRequesterApproval: true } } });
      Swal.fire("Guardado", "Política de aprobación actualizada.", "success");
      await loadPolicy(processType);
    } catch (e) { Swal.fire("Error", e?.message || "No se pudo guardar la política.", "error"); }
    finally { setSaving(false); }
  };

  const LEVEL_COLORS = ["linear-gradient(135deg,#ede9fe,#ddd6fe)", "linear-gradient(135deg,#fdf2f8,#fce7f3)", "linear-gradient(135deg,#eff6ff,#dbeafe)", "linear-gradient(135deg,#ecfdf5,#d1fae5)", "linear-gradient(135deg,#fff7ed,#fed7aa)"];

  return (
    <div className="min-vh-100" style={{ background: "#f5f3ff", padding: "24px 0" }}>
      <style>{`
        .pan-level:hover{box-shadow:0 6px 20px rgba(109,40,217,.12)!important;transform:translateY(-1px)}
        .pan-preset-btn{border:1.5px solid #c4b5fd!important;color:${PURPLE700}!important;background:#fff!important;border-radius:10px!important;font-weight:600!important;transition:all .15s!important}
        .pan-preset-btn:hover{background:${PURPLE100}!important;border-color:#7c3aed!important}
        .pan-chip .form-check-input{position:absolute;opacity:0;width:0;height:0;pointer-events:none}
        .pan-chip .form-check-label{display:inline-flex;align-items:center;gap:5px;background:#fff;border:1.5px solid #ddd6fe;border-radius:8px;padding:5px 12px;cursor:pointer;transition:all .15s;font-size:.82rem;font-weight:500;color:#4b5563;user-select:none;white-space:nowrap}
        .pan-chip .form-check-input:checked + .form-check-label{background:linear-gradient(90deg,#ede9fe,#ddd6fe);border-color:#7c3aed;color:#4c1d95;font-weight:700}
        .pan-chip .form-check-label:hover{border-color:#7c3aed;background:#ede9fe}
        .pan-chip .form-check-input:disabled + .form-check-label{cursor:not-allowed;opacity:.55}
        .pan-check-icon{font-size:.7rem;color:#7c3aed;font-weight:900}
        .pan-btn{transition:all .2s}
        .pan-btn:hover:not(:disabled){transform:scale(1.03);box-shadow:0 4px 14px rgba(109,40,217,.25)}
      `}</style>

      <Container fluid style={{ width: "94%", maxWidth: 1500 }}>
        <Card style={{ borderRadius: 15, overflow: "hidden", boxShadow: "0 8px 32px rgba(109,40,217,.1)", border: "none" }}>

          {/* Header */}
          <Card.Header className="text-white py-4 position-relative" style={{ background: GRADIENT, borderBottom: `3px solid ${ACCENT}`, borderRadius: "15px 15px 0 0" }}>
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center w-100">
              <h2 className="fw-bold mb-1">
                <FaSitemap className="me-3" size={26} />
                Niveles de Aprobación
              </h2>
              <p className="mb-0 opacity-75">
                Configura el flujo de aprobaciones por proceso para la empresa {tenantId || "–"}
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ background: "#faf9ff" }}>
            {!canManage && (
              <Alert className="mb-4 d-flex align-items-center gap-2" style={{ borderRadius: 12, border: "1px solid #fbbf24", background: "#fffbeb", color: "#92400e" }}>
                <FaLock color="#d97706" /><span>Solo <strong>Admin</strong> y <strong>SuperAdmin</strong> pueden modificar esta política.</span>
              </Alert>
            )}

            {/* Proceso + Presets */}
            <Card className="border-0 mb-4" style={{ background: "linear-gradient(135deg,#ede9fe,#faf5ff)", borderRadius: 14, boxShadow: "0 2px 12px rgba(109,40,217,.07)" }}>
              <Card.Body className="p-4">
                <Row className="g-3 align-items-end">
                  <Col md={5}>
                    <Form.Label className="fw-bold mb-1" style={{ color: PURPLE700, fontSize: ".9rem", textTransform: "uppercase", letterSpacing: ".04em" }}>Proceso</Form.Label>
                    <Form.Select value={processType} onChange={(e) => setProcessType(e.target.value)}
                      style={{ borderRadius: 10, borderColor: "#c4b5fd", fontSize: "1rem", fontWeight: 600, color: "#4c1d95", background: "#fff", padding: "10px 14px" }}>
                      {PROCESS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={7} className="d-flex align-items-end gap-2 flex-wrap">
                    <div>
                      <Form.Label className="fw-bold mb-1 d-block" style={{ color: PURPLE700, fontSize: ".9rem", textTransform: "uppercase", letterSpacing: ".04em" }}>Presets rápidos</Form.Label>
                      <ButtonGroup>
                        <Button className="pan-preset-btn" onClick={applySingleLevelAdminPreset} disabled={!canManage}>Solo Admin</Button>
                        <Button className="pan-preset-btn" onClick={applyTwoLevelRhAdminPreset} disabled={!canManage}>RH + Admin</Button>
                      </ButtonGroup>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Bypass switch */}
            <Card className="border-0 mb-4" style={{ background: "linear-gradient(135deg,#fdf2f8,#fce7f3)", borderRadius: 14, border: "1.5px solid #fbcfe8" }}>
              <Card.Body className="py-3 px-4 d-flex align-items-center gap-3">
                <div style={{ background: "rgba(236,72,153,.12)", borderRadius: 10, width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <FaShieldAlt color="#be185d" size={18} />
                </div>
                <div className="flex-grow-1">
                  <Form.Check
                    type="switch"
                    id="admin-bypass-switch"
                    label={<span style={{ fontWeight: 700, color: "#be185d" }}>Permitir bypass de Admin (aprueba y cierra el flujo en cualquier nivel)</span>}
                    checked={adminBypassEnabled}
                    onChange={(e) => setAdminBypassEnabled(e.target.checked)}
                    disabled={!canManage}
                  />
                  <small className="text-muted">Cuando está activo, se exige comentario al usar bypass.</small>
                </div>
              </Card.Body>
            </Card>

            {/* Niveles */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "#7c3aed", width: "3rem", height: "3rem" }} />
                <p className="mt-3 fw-semibold" style={{ color: PURPLE700 }}>Cargando política...</p>
              </div>
            ) : (
              <>
                <div className="d-flex flex-column gap-3">
                  {levels.map((lvl, idx) => (
                    <Card key={`lvl-${idx}`} className="pan-level border-0" style={{ borderRadius: 14, background: LEVEL_COLORS[idx % LEVEL_COLORS.length], boxShadow: "0 2px 10px rgba(109,40,217,.07)", transition: "all .2s", border: "1.5px solid #ede9fe" }}>
                      <Card.Body className="p-4">
                        {/* Nivel header */}
                        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ background: GRADIENT, borderRadius: 50, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 12px rgba(109,40,217,.3)" }}>
                              <span style={{ color: "#fff", fontWeight: 800, fontSize: "1.1rem" }}>{idx + 1}</span>
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold" style={{ color: "#1e1b4b", fontSize: "1rem" }}>Nivel {idx + 1}</h6>
                              <small style={{ color: "#6b7280" }}>{Array.isArray(lvl.roles) ? lvl.roles.length : 0} rol(es) asignado(s)</small>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ background: "rgba(255,255,255,.7)", borderRadius: 10, padding: "6px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                              <small style={{ color: PURPLE700, fontWeight: 600, whiteSpace: "nowrap" }}>Mín. aprobadores:</small>
                              <Form.Control
                                type="number"
                                min={1}
                                value={lvl.minApprovers}
                                onChange={(e) => updateLevel(idx, "minApprovers", e.target.value)}
                                disabled={!canManage}
                                style={{ width: 70, borderRadius: 8, borderColor: "#c4b5fd", textAlign: "center", fontWeight: 700, color: "#4c1d95", padding: "4px 8px" }}
                              />
                            </div>
                            <Button
                              size="sm"
                              onClick={() => removeLevel(idx)}
                              disabled={!canManage || levels.length <= 1}
                              style={{ background: "rgba(239,68,68,.1)", border: "1.5px solid #fca5a5", color: "#dc2626", borderRadius: 8, fontWeight: 600, padding: "6px 12px" }}
                            >
                              <FaTrash className="me-1" size={12} />Quitar
                            </Button>
                          </div>
                        </div>

                        <hr style={{ borderColor: "rgba(109,40,217,.15)", margin: "0 0 14px" }} />

                        <div>
                          <small className="fw-bold d-block mb-2" style={{ color: PURPLE700, textTransform: "uppercase", letterSpacing: ".05em", fontSize: ".78rem" }}>Roles que pueden aprobar</small>
                          <div className="d-flex flex-wrap gap-2">
                            {roleOptions.map((role) => {
                              const checked = Array.isArray(lvl.roles) && lvl.roles.includes(role.code);
                              return (
                                <div key={`${idx}-${role.code}`} className="pan-chip form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`chk-lvl${idx}-${role.code}`}
                                    checked={checked}
                                    onChange={(e) => toggleLevelRole(idx, role.code, e.target.checked)}
                                    disabled={!canManage}
                                  />
                                  <label className="form-check-label" htmlFor={`chk-lvl${idx}-${role.code}`}>
                                    {checked && <span className="pan-check-icon me-1">✓</span>}
                                    {role.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                  <Button className="pan-btn" onClick={addLevel} disabled={!canManage}
                    style={{ background: PURPLE100, border: `1.5px solid #c4b5fd`, color: PURPLE700, fontWeight: 700, borderRadius: 10, padding: "10px 20px" }}>
                    <FaPlus className="me-2" />Añadir nivel
                  </Button>
                  <Button className="pan-btn" onClick={savePolicy} disabled={!canManage || saving}
                    style={{ background: GRADIENT, border: "none", color: "#fff", fontWeight: 700, borderRadius: 10, padding: "10px 24px" }}>
                    <FaSave className="me-2" />{saving ? "Guardando..." : "Guardar política"}
                  </Button>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default AprobacionesNiveles;
